import RequestDescription from "./RequestDescription";
import RequestPreviewDialog from "./RequestPreviewDialog";
import {
  canResumeRequest,
  computeRequestStats,
  DESCRIPTION_PROMPT_STATUSES,
  downloadFileName,
} from "./TemplateRequestHistory.support";
import {
  PRIORITY_CONFIG,
  PRIORITY_ORDER,
  STATUS_CONFIG,
  STATUS_ORDER,
} from "./statusConfig";
import { usePortalUrlState } from "./usePortalUrlState";
import {
  HistoryRounded as HistoryIcon,
  ExpandMoreRounded as ExpandIcon,
  DownloadRounded as DownloadIcon,
  VisibilityRounded as PreviewIcon,
  AdminPanelSettingsRounded as AdminIcon,
  PersonRounded as UserIcon,
  FilterListRounded as FilterIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import * as dates from "common/dates";
import Button from "components/common/Button";
import CopyButton from "components/common/CopyButton";
import { saveFile } from "fileUtils";
import { useState, useMemo } from "react";

const AdminStatusControls = ({ request, onSaveRequest }) => {
  const [newStatus, setNewStatus] = useState(request.status);
  // A NEW message to append — NOT an edit of the stored request.description.
  const [message, setMessage] = useState("");

  const promptsForMessage = DESCRIPTION_PROMPT_STATUSES.has(newStatus);

  const handleStatusChange = (e) => {
    const status = e.target.value;
    setNewStatus(status);
    setMessage("");
    // Non-prompt statuses preserve today's fire-on-select behaviour. Prompt
    // statuses reveal the message UI below and defer saving until Save.
    if (!DESCRIPTION_PROMPT_STATUSES.has(status)) {
      onSaveRequest?.(request.id, { status });
    }
  };

  const handleSave = () => {
    const trimmed = message.trim();
    onSaveRequest?.(request.id, {
      status: newStatus,
      // The message is optional — only send it when one was actually typed.
      ...(trimmed ? { descriptionMessage: message } : {}),
    });
  };

  return (
    <Paper
      variant={"outlined"}
      sx={{
        p: 2,
        mb: 1,
        backgroundColor: "primary.lighter",
        borderColor: "primary.main",
      }}
    >
      <Stack direction={"row"} alignItems={"center"} gap={2}>
        <AdminIcon color={"primary"} />
        <Typography
          variant={"subtitle2"}
          sx={{ fontWeight: 600, color: "primary.main" }}
        >
          {"Admin Controls"}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <FormControl size={"small"} sx={{ minWidth: 180 }}>
          <InputLabel id={`status-select-label-${request.id}`}>
            {"Status"}
          </InputLabel>
          <Select
            labelId={`status-select-label-${request.id}`}
            id={`status-select-${request.id}`}
            value={newStatus}
            label={"Status"}
            onChange={handleStatusChange}
          >
            {STATUS_ORDER.map((key) => {
              const config = STATUS_CONFIG[key];
              return (
                <MenuItem key={key} value={key}>
                  <Stack direction={"row"} alignItems={"center"} gap={1}>
                    <Box sx={{ color: `${config.color}.main` }}>
                      {config.icon}
                    </Box>
                    {config.label}
                  </Stack>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Stack>

      {/* The stored description is rendered once, by the card's own
          RequestDescription; the admin only writes a NEW message here. */}
      {promptsForMessage && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <TextField
            label={"Message"}
            multiline
            minRows={3}
            fullWidth
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <Box>
            <Button
              variant={"contained"}
              color={"primary"}
              disableElevation
              onClick={handleSave}
            >
              {"Save"}
            </Button>
          </Box>
        </Stack>
      )}
    </Paper>
  );
};

const RequestCard = ({
  request,
  expanded,
  onToggle,
  onPreview,
  onContinueEdit,
  isAdmin,
  onSaveRequest,
  impersonateCompanyId,
}) => {
  const statusConfig = STATUS_CONFIG[request.status];
  const priorityConfig = PRIORITY_CONFIG[request.priority];
  const canResume = canResumeRequest(request, onContinueEdit);

  const hasPreviewData =
    request.placements?.length > 0 || request.annotations?.length > 0;

  // The footer only names a second person when there is one.
  const showLastUpdated =
    request.lastUpdatedBy && request.lastUpdatedBy !== request.submittedBy;

  return (
    <Paper
      variant={"outlined"}
      sx={{
        borderRadius: "8px",
        mb: 1,
        overflow: "hidden",
      }}
    >
      <Accordion
        expanded={expanded}
        onChange={onToggle}
        disableGutters
        square
        elevation={0}
        sx={{
          border: "none",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandIcon />}
          sx={{
            // The caret leads, and the content's height is pinned across both
            // states so expanding moves nothing but the caret.
            flexDirection: "row-reverse",
            minHeight: 64,
            "&.Mui-expanded": { minHeight: 64 },
            "& .MuiAccordionSummary-content": { my: 1.5, ml: 1 },
            "& .MuiAccordionSummary-content.Mui-expanded": { my: 1.5, ml: 1 },
          }}
        >
          <Stack sx={{ flex: 1, pr: 2, minWidth: 0 }}>
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              gap={2}
              sx={{ mb: 0.5 }}
            >
              <Typography
                variant={"subtitle1"}
                sx={{ fontWeight: 600, minWidth: 0 }}
              >
                {request.title}
              </Typography>
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"flex-end"}
                gap={1}
                flexWrap={"wrap"}
              >
                {hasPreviewData && (
                  <Chip
                    icon={<PreviewIcon sx={{ fontSize: 14 }} />}
                    label={"Has Preview"}
                    size={"small"}
                    color={"info"}
                    variant={"outlined"}
                    clickable
                    sx={{ height: 24, fontSize: "0.7rem" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onPreview(request);
                    }}
                  />
                )}
                {request.s3FileId && (
                  <Chip
                    icon={<DownloadIcon sx={{ fontSize: 14 }} />}
                    label={"Download"}
                    size={"small"}
                    color={"primary"}
                    variant={"outlined"}
                    clickable
                    sx={{ height: 24, fontSize: "0.7rem" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      saveFile(
                        request.s3FileId,
                        downloadFileName(request),
                        impersonateCompanyId
                      );
                    }}
                  />
                )}
                <Chip
                  icon={statusConfig?.icon}
                  label={statusConfig?.label || request.status}
                  size={"small"}
                  color={statusConfig?.color || "default"}
                />
                <Chip
                  label={priorityConfig?.label || request.priority}
                  size={"small"}
                  color={priorityConfig?.color || "default"}
                  variant={"outlined"}
                />
              </Stack>
            </Stack>
            <Stack
              direction={"row"}
              alignItems={"center"}
              gap={2}
              flexWrap={"wrap"}
            >
              <Typography
                variant={"caption"}
                color={"text.secondary"}
                sx={{ whiteSpace: "nowrap" }}
              >
                {request.id}
              </Typography>
              <CopyButton
                content={request.id}
                tooltip={"Copy REQ"}
                scale={"small"}
              />
              {request.formNumber && (
                <Typography
                  variant={"caption"}
                  color={"text.secondary"}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {`• ${request.formNumber}`}
                </Typography>
              )}
              {request.templateName && (
                <Typography
                  variant={"caption"}
                  color={"text.secondary"}
                  sx={{
                    whiteSpace: "nowrap",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {`• ${request.templateName}`}
                </Typography>
              )}
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {/* Everything stacked in the expanded card is 8px apart, matching
              the gap between the cards themselves. */}
          <Divider sx={{ mb: 1 }} />

          {/* Admin Controls - Only visible to admins */}
          {isAdmin && (
            <AdminStatusControls
              request={request}
              onSaveRequest={onSaveRequest}
            />
          )}

          {/* Status Banner */}
          {statusConfig && (
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                mb: 1,
                backgroundColor: `${statusConfig?.color}.main`,
                color: "white",
                borderRadius: 1,
              }}
            >
              <Stack direction={"row"} alignItems={"center"} gap={1}>
                {statusConfig?.icon}
                <Typography variant={"body2"} sx={{ fontWeight: 500 }}>
                  {statusConfig?.description}
                </Typography>
              </Stack>
            </Paper>
          )}

          {/* The shared wrapper renders each button inside an inline span and
              drops `fullWidth`, so two of them would sit side by side with no
              gap and a margin that lands inside the span. A Stack makes each
              span a block-level flex item and owns the spacing; `width: 100%`
              is what actually fills the row, since `fullWidth` never arrives.

              Preview is the secondary action of the two, distinguished by
              colour rather than variant, which the wrapper does not forward. */}
          {(hasPreviewData || canResume) && (
            <Stack spacing={1} sx={{ mb: 1 }}>
              {hasPreviewData && (
                <Button
                  color={"secondary"}
                  startIcon={<PreviewIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPreview(request);
                  }}
                  sx={{ width: "100%" }}
                >
                  {"View Document Preview with Annotations & Field Placements"}
                </Button>
              )}
              {canResume && (
                <Button
                  color={"primary"}
                  onClick={(e) => {
                    e.stopPropagation();
                    onContinueEdit(request);
                  }}
                  sx={{ width: "100%" }}
                >
                  {"Edit Annotations & Field Placements"}
                </Button>
              )}
            </Stack>
          )}

          {/* Request Details */}
          <Stack spacing={1}>
            <RequestDescription request={request} />

            {request.selectedFields?.length > 0 && (
              <Box>
                <Typography
                  variant={"overline"}
                  color={"text.secondary"}
                  sx={{ display: "block" }}
                >
                  {"Mapped Fields"}
                </Typography>
                <Stack direction={"row"} gap={0.5} flexWrap={"wrap"}>
                  {request.selectedFields.map((field) => (
                    <Chip
                      key={field}
                      label={field}
                      size={"small"}
                      variant={"outlined"}
                      sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Custom Fields if present */}
            {request.customFields?.length > 0 && (
              <Box>
                <Typography
                  variant={"overline"}
                  color={"text.secondary"}
                  sx={{ display: "block" }}
                >
                  {"Custom Fields Requested"}
                </Typography>
                <Stack direction={"row"} gap={0.5} flexWrap={"wrap"}>
                  {request.customFields.map((field) => (
                    <Chip
                      key={field.id}
                      label={field.label}
                      size={"small"}
                      color={"warning"}
                      variant={"outlined"}
                      sx={{ fontSize: "0.75rem" }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {request.s3FileId && (
              <Box>
                <Typography
                  variant={"overline"}
                  color={"text.secondary"}
                  sx={{ display: "block" }}
                >
                  {"Attachments"}
                </Typography>
                <Stack direction={"row"} gap={0.5} flexWrap={"wrap"}>
                  <Chip
                    icon={<DownloadIcon />}
                    label={request.templateName || "Download File"}
                    size={"small"}
                    variant={"outlined"}
                    clickable
                    onClick={(e) => {
                      e.stopPropagation();
                      saveFile(
                        request.s3FileId,
                        downloadFileName(request),
                        impersonateCompanyId
                      );
                    }}
                  />
                </Stack>
              </Box>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Attribution sits outside the Collapse, so it reads whether the card
          is open or shut. */}
      <Divider />
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        gap={2}
        sx={{ px: 2, py: 1 }}
      >
        {/* The address itself, not a name guessed from it: the backend holds
            no name, and inferring one from the local part is wrong as often
            as it is right. */}
        <Typography variant={"caption"} color={"text.secondary"}>
          {`Submitted by ${request.submittedBy} on ${dates.asOfDate(
            request.submittedAt
          )}`}
        </Typography>
        {showLastUpdated && (
          <Typography variant={"caption"} color={"text.secondary"}>
            {`Last updated by ${request.lastUpdatedBy} on ${dates.asOfDate(
              request.updatedAt
            )}`}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

const TemplateRequestHistory = ({
  submittedRequests = [],
  currentUserEmail,
  isAdmin = false,
  onSaveRequest,
  onContinueEdit,
  impersonateCompanyId,
  isLoading = false,
}) => {
  const [expandedId, setExpandedId] = useState(null);
  const [previewRequest, setPreviewRequest] = useState(null);
  const [viewMode, setViewMode] = useState(isAdmin ? "all" : "mine");
  // Selected status and priority filters. An empty array means "all" (no
  // filter of that kind). Both live in the query string so a narrowed list is
  // a link someone can send; multiple keys are selected via cmd/ctrl/shift.
  const {
    statuses: selectedStatuses,
    setStatuses: setSelectedStatuses,
    priorities: selectedPriorities,
    setPriorities: setSelectedPriorities,
  } = usePortalUrlState();

  const isAllSelected = selectedStatuses.length === 0;
  const isStatusSelected = (key) => selectedStatuses.includes(key);
  const isAllPrioritiesSelected = selectedPriorities.length === 0;
  const isPrioritySelected = (key) => selectedPriorities.includes(key);
  // An empty list is a filtered-out list, not an empty account, whichever
  // filter did the narrowing.
  const hasActiveFilter = !isAllSelected || !isAllPrioritiesSelected;

  // Plain click selects a single key (replacing any selection); a
  // cmd/ctrl/shift click toggles the key in/out of a multi-selection.
  const keyClickHandler = (setKeys) => (key, event) => {
    const multi = event && (event.metaKey || event.ctrlKey || event.shiftKey);
    setKeys((prev) => {
      if (!multi) {
        return [key];
      }
      return prev.includes(key)
        ? prev.filter((s) => s !== key)
        : [...prev, key];
    });
  };

  const handleStatusClick = keyClickHandler(setSelectedStatuses);
  const handlePriorityClick = keyClickHandler(setSelectedPriorities);

  const handleSelectAll = () => setSelectedStatuses([]);
  const handleSelectAllPriorities = () => setSelectedPriorities([]);

  const handleToggle = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleOpenPreview = (request) => {
    setPreviewRequest(request);
  };

  const handleClosePreview = () => {
    setPreviewRequest(null);
  };

  const allRequests = useMemo(() => {
    return [...submittedRequests];
  }, [submittedRequests]);

  // Requests in scope for the current viewer (admin sees all; otherwise only
  // the user's own). The status-filter chips/cards filter WITHIN this set, so
  // the stat counts are derived from here and do not change as the status
  // filter changes.
  const scopedRequests = useMemo(() => {
    if (!isAdmin || viewMode === "mine") {
      return allRequests.filter(
        (r) => r.submittedBy === currentUserEmail || !r.submittedBy
      );
    }
    return allRequests;
  }, [allRequests, isAdmin, viewMode, currentUserEmail]);

  // Priority applies before the statistics, so the cards count only the
  // priorities in view. Status applies after, selecting within those counts —
  // which is why the priority filter sits above the cards and status below.
  const prioritisedRequests = useMemo(() => {
    if (selectedPriorities.length === 0) {
      return scopedRequests;
    }
    return scopedRequests.filter((r) =>
      selectedPriorities.includes(r.priority)
    );
  }, [scopedRequests, selectedPriorities]);

  const filteredRequests = useMemo(() => {
    if (selectedStatuses.length === 0) {
      return prioritisedRequests;
    }
    return prioritisedRequests.filter((r) =>
      selectedStatuses.includes(r.status)
    );
  }, [prioritisedRequests, selectedStatuses]);

  const activeRequests = filteredRequests.filter(
    (r) => !["COMPLETED", "CANCELLED"].includes(r.status)
  );
  const completedRequests = filteredRequests.filter((r) =>
    ["COMPLETED", "CANCELLED"].includes(r.status)
  );

  const stats = useMemo(() => computeRequestStats(prioritisedRequests), [
    prioritisedRequests,
  ]);

  const renderTotalCard = () => (
    <Paper
      variant={isAllSelected ? "elevation" : "outlined"}
      elevation={isAllSelected ? 4 : 0}
      onClick={handleSelectAll}
      sx={{
        flex: 1,
        p: 2,
        textAlign: "center",
        cursor: "pointer",
        userSelect: "none",
        border: isAllSelected ? 2 : 1,
        borderColor: isAllSelected ? "primary.main" : "divider",
      }}
    >
      <Typography
        variant={"h4"}
        color={"primary.main"}
        sx={{ fontWeight: 600 }}
      >
        {isLoading ? <Skeleton /> : stats.total}
      </Typography>
      <Typography variant={"body2"} color={"text.secondary"}>
        {"Total Requests"}
      </Typography>
    </Paper>
  );

  const renderStatusCard = (key) => {
    const config = STATUS_CONFIG[key];
    return (
      <Paper
        key={key}
        variant={isStatusSelected(key) ? "elevation" : "outlined"}
        elevation={isStatusSelected(key) ? 4 : 0}
        onClick={(e) => handleStatusClick(key, e)}
        sx={{
          flex: 1,
          p: 2,
          textAlign: "center",
          cursor: "pointer",
          userSelect: "none",
          border: isStatusSelected(key) ? 2 : 1,
          borderColor: isStatusSelected(key)
            ? config.color === "default"
              ? "text.secondary"
              : `${config.color}.main`
            : "divider",
        }}
      >
        <Typography
          variant={"h4"}
          color={config.color === "default" ? "text.secondary" : config.color}
          sx={{ fontWeight: 600 }}
        >
          {isLoading ? <Skeleton /> : stats[key] ?? 0}
        </Typography>
        <Typography variant={"body2"} color={"text.secondary"}>
          {config.label}
        </Typography>
      </Paper>
    );
  };

  return (
    <Box>
      {/* Admin View Toggle */}
      {isAdmin && (
        <Alert
          severity={"info"}
          icon={<AdminIcon />}
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 2,
          }}
          action={
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, val) => val && setViewMode(val)}
              size={"small"}
            >
              <ToggleButton value={"all"}>
                <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                  <AdminIcon fontSize={"small"} />
                  {"All Requests"}
                </Stack>
              </ToggleButton>
              <ToggleButton value={"mine"}>
                <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                  <UserIcon fontSize={"small"} />
                  {"My Requests"}
                </Stack>
              </ToggleButton>
            </ToggleButtonGroup>
          }
        >
          <Typography variant={"body2"}>
            {
              "You have admin access. You can view and manage all template requests."
            }
          </Typography>
        </Alert>
      )}

      {/* Priority sits above the statistics because it narrows what they
          count, unlike the status filter below, which selects within them. */}
      <Paper variant={"outlined"} sx={{ p: 1.5, mb: 2 }}>
        <Stack direction={"row"} alignItems={"center"} gap={2}>
          <FilterIcon color={"action"} />
          <Typography variant={"body2"} sx={{ fontWeight: 500 }}>
            {"Filter by Priority:"}
          </Typography>
          <Stack direction={"row"} gap={0.5} flexWrap={"wrap"}>
            <Chip
              label={"All"}
              size={"small"}
              onClick={handleSelectAllPriorities}
              color={isAllPrioritiesSelected ? "primary" : "default"}
              variant={isAllPrioritiesSelected ? "filled" : "outlined"}
            />
            {PRIORITY_ORDER.map((key) => {
              const config = PRIORITY_CONFIG[key];
              return (
                <Chip
                  key={key}
                  label={config.label}
                  size={"small"}
                  onClick={(e) => handlePriorityClick(key, e)}
                  color={isPrioritySelected(key) ? config.color : "default"}
                  variant={isPrioritySelected(key) ? "filled" : "outlined"}
                />
              );
            })}
          </Stack>
        </Stack>
      </Paper>

      {/* Header Stats — two rows. Top: the in-flight statuses; bottom: the
          remaining statuses followed by the Total Requests card. */}
      <Stack direction={"row"} spacing={2} sx={{ mb: 2 }}>
        {STATUS_ORDER.slice(0, 5).map((key) => renderStatusCard(key))}
      </Stack>
      <Stack direction={"row"} spacing={2} sx={{ mb: 3 }}>
        {STATUS_ORDER.slice(5).map((key) => renderStatusCard(key))}
        {renderTotalCard()}
      </Stack>

      {/* Status Filter */}
      <Paper variant={"outlined"} sx={{ p: 1.5, mb: 2 }}>
        <Stack direction={"row"} alignItems={"center"} gap={2}>
          <FilterIcon color={"action"} />
          <Typography variant={"body2"} sx={{ fontWeight: 500 }}>
            {"Filter by Status:"}
          </Typography>
          <Stack direction={"row"} gap={0.5} flexWrap={"wrap"}>
            <Chip
              label={"All"}
              size={"small"}
              onClick={handleSelectAll}
              color={isAllSelected ? "primary" : "default"}
              variant={isAllSelected ? "filled" : "outlined"}
            />
            {STATUS_ORDER.map((key) => {
              const config = STATUS_CONFIG[key];
              return (
                <Chip
                  key={key}
                  icon={config.icon}
                  label={config.label}
                  size={"small"}
                  onClick={(e) => handleStatusClick(key, e)}
                  color={isStatusSelected(key) ? config.color : "default"}
                  variant={isStatusSelected(key) ? "filled" : "outlined"}
                  sx={{
                    "& .MuiChip-icon": {
                      marginLeft: "7px",
                      marginRight: "-2px",
                    },
                  }}
                />
              );
            })}
          </Stack>
        </Stack>
      </Paper>

      {/* Active Requests */}
      {activeRequests.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant={"subtitle1"} sx={{ fontWeight: 600, mb: 1.5 }}>
            {"Active Requests"}
          </Typography>
          {activeRequests.map((request) => (
            <RequestCard
              key={request.id}
              request={request}
              expanded={expandedId === request.id}
              onToggle={() => handleToggle(request.id)}
              onPreview={handleOpenPreview}
              onContinueEdit={onContinueEdit}
              isAdmin={isAdmin}
              onSaveRequest={onSaveRequest}
              impersonateCompanyId={impersonateCompanyId}
            />
          ))}
        </Box>
      )}

      {/* Completed Requests */}
      {completedRequests.length > 0 && (
        <Box>
          <Typography variant={"subtitle1"} sx={{ fontWeight: 600, mb: 1.5 }}>
            {"Past Requests"}
          </Typography>
          {completedRequests.map((request) => (
            <RequestCard
              key={request.id}
              request={request}
              expanded={expandedId === request.id}
              onToggle={() => handleToggle(request.id)}
              onPreview={handleOpenPreview}
              onContinueEdit={onContinueEdit}
              isAdmin={isAdmin}
              onSaveRequest={onSaveRequest}
              impersonateCompanyId={impersonateCompanyId}
            />
          ))}
        </Box>
      )}

      {/* Empty State. An in-flight query has no requests yet but that is not
          the same as having none, so it must not read as "none": while
          loading, the skeleton stats hold the page and nothing is claimed. */}
      {!isLoading && filteredRequests.length === 0 && (
        <Paper
          variant={"outlined"}
          sx={{
            p: 6,
            textAlign: "center",
            borderStyle: "dashed",
          }}
        >
          <HistoryIcon sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
          <Typography variant={"h6"} sx={{ mb: 1 }}>
            {"No requests found"}
          </Typography>
          <Typography variant={"body2"} color={"text.secondary"}>
            {/* The chips above already show what is selected, so the message
                names the cause rather than reciting the selection back. */}
            {hasActiveFilter
              ? "No requests match the current filters."
              : viewMode === "mine"
              ? "You haven't submitted any template requests yet."
              : "No template requests have been submitted yet."}
          </Typography>
        </Paper>
      )}

      {/* Preview Dialog */}
      <RequestPreviewDialog
        open={Boolean(previewRequest)}
        onClose={handleClosePreview}
        request={previewRequest}
        impersonateCompanyId={impersonateCompanyId}
      />
    </Box>
  );
};

export default TemplateRequestHistory;
